// Design System Configuration
export const theme = {
  // Colors - Spiritual Theme
  colors: {
    primary: {
      50: 'orange-50',
      100: 'orange-100',
      200: 'orange-200',
      300: 'orange-300',
      400: 'orange-400',
      500: 'orange-500',
      600: 'orange-600',
      700: 'orange-700',
      800: 'orange-800',
      900: 'orange-900',
    },
    secondary: {
      50: 'red-50',
      100: 'red-100',
      200: 'red-200',
      300: 'red-300',
      400: 'red-400',
      500: 'red-500',
      600: 'red-600',
      700: 'red-700',
      800: 'red-800',
      900: 'red-900',
    },
    success: {
      50: 'green-50',
      100: 'green-100',
      200: 'green-200',
      300: 'green-300',
      400: 'green-400',
      500: 'green-500',
      600: 'green-600',
      700: 'green-700',
      800: 'green-800',
      900: 'green-900',
    },
    spiritual: {
      saffron: 'orange-500',
      vermillion: 'red-500',
      turmeric: 'yellow-400',
      sacred: 'amber-600',
      peace: 'green-500',
      divine: 'purple-600',
    },
    error: {
      50: 'red-50',
      200: 'red-200',
      400: 'red-400',
      500: 'red-500',
      600: 'red-600',
      700: 'red-700',
      800: 'red-800',
    },
    warning: {
      50: 'yellow-50',
      200: 'yellow-200',
      400: 'yellow-400',
      800: 'yellow-800',
    },
    info: {
      50: 'blue-50',
      200: 'blue-200',
      400: 'blue-400',
      800: 'blue-800',
    },
    neutral: {
      50: 'gray-50',
      100: 'gray-100',
      200: 'gray-200',
      300: 'gray-300',
      700: 'gray-700',
      900: 'gray-900',
    }
  },

  // Spacing
  spacing: {
    xs: '1',
    sm: '2',
    md: '4',
    lg: '6',
    xl: '8',
    '2xl': '12',
  },

  // Typography
  typography: {
    sizes: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
    },
    weights: {
      medium: 'font-medium',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
    }
  },

  // Border radius
  borderRadius: {
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  },

  // Shadows
  shadows: {
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
  },

  // Layout
  layout: {
    maxWidths: {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '7xl': 'max-w-7xl',
    },
    minHeights: {
      screen: 'min-h-screen',
    }
  }
};

// Component Variants
export const componentVariants = {
  button: {
    base: 'inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
    variants: {
      primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
      secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-900 focus:ring-gray-500',
      outline: 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-blue-500',
      danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
      success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500'
    },
    sizes: {
      small: 'px-3 py-2 text-sm',
      medium: 'px-4 py-2 text-sm',
      large: 'px-6 py-3 text-base'
    }
  },

  input: {
    base: 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
    error: 'border-red-500 focus:ring-red-500 focus:border-red-500',
    label: 'block text-sm font-medium text-gray-700 mb-2',
    errorText: 'mt-1 text-sm text-red-600',
    required: 'text-red-500 ml-1'
  },

  card: {
    base: 'bg-white rounded-lg shadow-md border border-gray-200',
    header: 'px-6 py-4 border-b border-gray-200',
    content: 'px-6 py-4',
    footer: 'px-6 py-4 border-t border-gray-200'
  },

  alert: {
    base: 'border rounded-md p-4',
    types: {
      success: {
        container: `bg-${theme.colors.success[50]} border-${theme.colors.success[200]} text-${theme.colors.success[800]}`,
        icon: '✓',
        iconColor: `text-${theme.colors.success[400]}`
      },
      error: {
        container: `bg-${theme.colors.error[50]} border-${theme.colors.error[200]} text-${theme.colors.error[800]}`,
        icon: '✕',
        iconColor: `text-${theme.colors.error[400]}`
      },
      warning: {
        container: `bg-${theme.colors.warning[50]} border-${theme.colors.warning[200]} text-${theme.colors.warning[800]}`,
        icon: '⚠',
        iconColor: `text-${theme.colors.warning[400]}`
      },
      info: {
        container: `bg-${theme.colors.info[50]} border-${theme.colors.info[200]} text-${theme.colors.info[800]}`,
        icon: 'ℹ',
        iconColor: `text-${theme.colors.info[400]}`
      }
    }
  },

  layout: {
    authLayout: {
      container: 'min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8',
      header: 'sm:mx-auto sm:w-full sm:max-w-md',
      title: 'text-3xl font-extrabold text-gray-900',
      subtitle: 'mt-2 text-sm text-gray-600',
      content: 'mt-8 sm:mx-auto sm:w-full sm:max-w-md',
      cardContent: 'py-8'
    },
    pageLayout: {
      container: 'min-h-screen bg-gray-50',
      header: 'bg-white shadow',
      headerContent: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
      main: 'max-w-7xl mx-auto py-6 sm:px-6 lg:px-8'
    }
  }
};
